import React from "react";
import styled from "styled-components";

import { useIntl } from "../../../../shared/core/i18n/use-intl";
import type { Account } from "../../../../shared/domains/account/account";
import { getSettlementBalance } from "../../../../shared/domains/account/account";
import { theme } from "../../styles/theme";
import { AmountText } from "../amount-text";
import { UnstyledButton } from "../buttons/default-button";
import { ChevronIcon } from "../svg/chevron";
import { PageHeaderBand, PageHeaderTitle } from "./page-header-band";

interface PageBackHeaderProps {
  title: string;
  subtitle: string;
  /** Given an account, the band also carries its number and balance, on the right. */
  account?: Account;
  onBack: () => void;
}

/** The header band of the screens opened from somewhere else: a way back, and what they are about. */
/** The way back the screens opened from somewhere else carry, above whatever names them. */
export function PageBackLink({ className, onBack }: Readonly<{ className?: string; onBack: () => void }>) {
  const { formatMessage } = useIntl();

  return (
    <BackLink type="button" className={className} onClick={onBack}>
      <BackChevron width={10} height={10} />
      {formatMessage("registerScreen.progressBackLink")}
    </BackLink>
  );
}

export function PageBackHeader({ title, subtitle, account, onBack }: Readonly<PageBackHeaderProps>) {
  const { formatMessage } = useIntl();

  return (
    <PageHeaderBand>
      <HeaderRow>
        <Texts>
          <PageBackLink onBack={onBack} />
          <PageHeaderTitle>{title}</PageHeaderTitle>
          <Subtitle>{subtitle}</Subtitle>
        </Texts>
        {!!account && (
          <BalanceBlock>
            <BalanceLabel>
              {formatMessage("homeScreen.accountBalance")} : {formatMessage("account.accountId", { id: account.id })}
            </BalanceLabel>
            <Balance amount={getSettlementBalance(account)} />
          </BalanceBlock>
        )}
      </HeaderRow>
    </PageHeaderBand>
  );
}

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
`;

const Texts = styled.div`
  display: flex;
  flex-direction: column;
`;

const BackLink = styled(UnstyledButton)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  font-size: 0.8125rem;
  color: ${theme.colors.blue[700]};
`;

/* The chevron points back: the icon's own path faces forward. */
const BackChevron = styled(ChevronIcon)`
  fill: ${theme.colors.blue[700]};
  transform: rotate(180deg);
`;

const Subtitle = styled.span`
  ${theme.text};
  font-size: 16px;
  line-height: 24px;
  color: ${theme.colors.gray[800]};
`;

const BalanceBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
`;

const BalanceLabel = styled.span`
  ${theme.text};
  font-size: 13px;
  line-height: 18px;
  color: ${theme.colors.gray[800]};
`;

const Balance = styled(AmountText)`
  ${theme.boldText};
  margin-top: 4px;
  font-size: 28px;
  line-height: 34px;
  color: ${theme.colors.blue[800]};
`;
